import api from 'services/api';
import { orderMembers } from 'lib/members';
import { getPermissions } from 'lib/permissions';
import { HomeResponse } from 'data/homeHub/api';
import { HomeHubData } from 'data/homeHub/redux';
import { MemberId, RegularMember } from 'types';
import { OnboardDataType } from 'data/homeHub/useOnboardHomeHub';
import type { CSRFClient } from 'lib/csrf';

export type RemoveMemberDataType = {
  id: MemberId;
};

export const appToApi = (data: any) => ({
  ...data,
  familyName: data.name,
});

export const apiToApp = (data: HomeResponse): HomeHubData => ({
  ...data,

  userCountry: data.members
    ? (data.members.find(m => m.isLoggedInUser) as RegularMember | undefined)
        ?.country ?? ''
    : '',

  members: orderMembers(data.members),
  permissions: getPermissions(data.accessControl, data.features),
});

export const loadHomeHub = (retries: number, csrfClient: CSRFClient) => {
  return csrfClient
    .exponentialRetryWithTokenUpdate(
      api,
      {
        method: 'GET',
        url: 'home/',
      },
      retries,
      1000,
    )
    .then(({ data }) => apiToApp(data));
};

export const onboardHomeHub = (
  updatedData: OnboardDataType,
  csrfClient: CSRFClient,
) => {
  return csrfClient
    .retryWithToken(api, {
      method: 'POST',
      url: 'home/onboard',
      data: updatedData,
      headers: { 'content-type': 'application/json' },
    })
    .then(({ data }) => apiToApp(data));
};

export const changeHomeAddress = (
  addressData: { address: string; placeId: string },
  csrfClient: CSRFClient,
) => {
  const { address, placeId } = addressData;
  return csrfClient
    .retryWithToken(api, {
      method: 'PATCH',
      url: 'home/',
      data: { address, placeId },
      headers: { 'content-type': 'application/json' },
    })
    .then(({ data }) => apiToApp(data));
};

type JoinPayload = {
  address: string;
  placeId: string;
  inviteToken: string;
};

const joinWithRetry = (payload: JoinPayload, csrfClient: CSRFClient) => {
  const isValidResponse = (status: any) => status >= 200 && status < 300;

  return csrfClient
    .exponentialRetryWithToken(
      api,
      {
        url: 'member/',
        method: 'POST',
        data: payload,
        validateStatus: status => status !== 409,
        headers: { 'content-type': 'application/json' },
      },
      3,
      2000,
    )
    .then(response =>
      response && isValidResponse(response.status)
        ? response
        : Promise.reject({ response }),
    )
    .then(({ data }) => apiToApp(data));
};

export const joinHomeHubWithAddress = (
  { address, placeId, inviteToken }: JoinPayload,
  csrfClient: CSRFClient,
) => joinWithRetry({ address, placeId, inviteToken }, csrfClient);

export const removeFromHomeHub = (
  { id }: RemoveMemberDataType,
  csrfClient: CSRFClient,
) => {
  return csrfClient
    .retryWithToken(api, {
      method: 'DELETE',
      url: `member/${id}`,
      data: { foo: 'bar' }, // Without a fake payload Content-Type is removed by axios
      headers: { 'Content-Type': 'application/json' },
    })
    .then(({ data }) => apiToApp(data));
};

export const changeParentalControl = (
  targetMemberId: MemberId,
  parentalControlLock: boolean,
) => {
  return api.post('parentalcontrol/', {
    targetMemberId,
    parentalControlLock,
  });
};
